import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  CameraIcon,
  EnvelopeIcon,
  MapPinIcon,
  BriefcaseIcon,
  IdentificationIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import EmployeeService from '../services/employee.service';
import toast from '../utils/toast';
import { formatDateDisplay } from '../utils/dateDisplay';

const formatDetailDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : formatDateDisplay(d);
};

const getRoleColor = (role) => {
  const r = (role || '').toLowerCase();
  switch (r) {
    case 'admin':
      return 'bg-purple-100 text-purple-800';
    case 'hr':
      return 'bg-blue-100 text-blue-800';
    case 'manager':
      return 'bg-orange-100 text-orange-800';
    case 'team_lead':
      return 'bg-cyan-100 text-cyan-900';
    case 'employee':
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const formatRoleLabel = (role) => {
  if (!role) return '—';
  const r = String(role);
  if (r === 'team_lead') return 'Team lead';
  return r.charAt(0).toUpperCase() + r.slice(1).toLowerCase();
};

const userInitials = (user) => {
  const a = (user?.first_name || '').trim().charAt(0);
  const b = (user?.last_name || '').trim().charAt(0);
  return (a + b).toUpperCase() || '?';
};

/** Turn stored path (e.g. /uploads/...) into a browser-loadable URL when API is on another origin. */
function resolveProfilePhotoUrl(pathOrUrl) {
  if (!pathOrUrl) return '';
  const s = String(pathOrUrl);
  if (s.startsWith('http://') || s.startsWith('https://') || s.startsWith('data:')) return s;
  const base = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000').replace(/\/$/, '');
  return `${base}${s.startsWith('/') ? s : `/${s}`}`;
}

/** Map GET /api/employee-info payload into auth user fields Profile expects. */
function employeeInfoToUserPatch(emp) {
  if (!emp) return {};
  const email = emp.email_address || emp.email;
  return {
    email,
    email_address: email,
    empcode: emp.empcode,
    first_name: emp.first_name,
    last_name: emp.last_name,
    role: emp.role,
    profile_picture: emp.profile_picture,
    designation: emp.designation,
    date_of_joining: emp.date_of_joining,
    updatedAt: emp.updatedAt ?? emp.updated_at,
    updated_at: emp.updated_at ?? emp.updatedAt,
    phone_no: emp.phone_no,
    phone: emp.phone_no,
    address: emp.address,
    is_active: emp.is_active,
    team: emp.team
  };
}

const Profile = () => {
  const { user, patchUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const userEmail = user?.email ?? user?.email_address ?? null;

  useEffect(() => {
    setAvatarFailed(false);
  }, [userEmail, user?.profile_picture]);

  useEffect(() => {
    if (!userEmail) return;

    let cancelled = false;
    (async () => {
      try {
        const emp = await EmployeeService.getCurrentUser();
        if (cancelled) return;
        patchUser(employeeInfoToUserPatch(emp));
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load profile details:', err);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userEmail, patchUser]);

  if (!user) {
    return null;
  }

  const displayName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  const email = user.email || user.email_address || '';
  const phoneDisplay = user.phone || user.phone_no;
  const department = user.department || user.team?.team_name || '';
  const address = user.address || '';
  const isActive = (user.is_active ?? user.isActive ?? true) === true;
  const photoSrc = resolveProfilePhotoUrl(user.profile_picture);
  const showProfilePhoto = Boolean(photoSrc) && !avatarFailed;
  const teamName = user.team?.team_name;
  const joinRaw = user.date_of_joining;
  const updatedAt = user.updatedAt || user.updated_at;

  const handlePhotoSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    setUploadingPhoto(true);
    try {
      const data = await EmployeeService.uploadProfilePicture(file);
      const url = data.profilePictureUrl;
      if (!url) {
        throw new Error('No image URL returned from server');
      }
      patchUser({ profile_picture: url });
      toast.success(data.message || 'Profile photo updated');
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to upload profile photo';
      toast.error(msg);
    } finally {
      setUploadingPhoto(false);
    }
  };

  return (
    <div className="space-y-6">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        className="hidden"
        aria-hidden
        onChange={handlePhotoSelected}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
      >
        <div className="relative bg-gradient-to-b from-slate-50 via-white to-white px-4 pt-8 pb-6 sm:px-8">
          <div className="flex flex-col items-center gap-6 md:flex-row md:items-start">
            <div className="relative shrink-0">
              {showProfilePhoto ? (
                <img
                  src={photoSrc}
                  alt=""
                  className="h-24 w-24 rounded-2xl border-4 border-white object-cover shadow-md"
                  onError={() => setAvatarFailed(true)}
                />
              ) : (
                <div
                  className="flex h-24 w-24 items-center justify-center rounded-2xl border-4 border-white bg-blue-100 text-2xl font-bold text-blue-800 shadow-md"
                  aria-hidden
                >
                  {userInitials(user)}
                </div>
              )}
              <button
                type="button"
                disabled={uploadingPhoto}
                onClick={() => fileInputRef.current?.click()}
                className="absolute -bottom-1 -right-1 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
                title={uploadingPhoto ? 'Uploading…' : 'Change profile photo'}
              >
                <CameraIcon className="w-4 h-4 text-gray-600" />
              </button>
              <div
                className={`absolute bottom-1 left-1 h-4 w-4 rounded-full border-2 border-white ${
                  isActive ? 'bg-emerald-500' : 'bg-red-500'
                }`}
                title={isActive ? 'Active' : 'Inactive'}
                aria-hidden
              />
            </div>

            <div className="min-w-0 flex-1 text-center md:text-left">
              <div className="flex flex-col items-center gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                  <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                    {displayName || '—'}
                  </h1>
                  <p className="mt-1 text-lg font-medium text-slate-600">
                    {user.designation || 'No designation on file'}
                  </p>
                </div>
                <span
                  className={`shrink-0 rounded-full px-3 py-1 text-sm font-semibold capitalize ${
                    isActive
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-red-100 text-red-700'
                  }`}
                >
                  {isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className="mt-5 flex flex-wrap justify-center gap-3 md:justify-start">
                <button
                  type="button"
                  onClick={() => navigate('/settings?tab=personal&edit=1')}
                  className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-br from-blue-600 to-blue-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:from-blue-700 hover:to-blue-600"
                >
                  <PencilIcon className="h-5 w-5" aria-hidden />
                  Edit profile
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 bg-white px-4 py-8 sm:px-8 lg:grid-cols-3">
          <div className="space-y-10 lg:col-span-2">
            <section className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm sm:p-6">
              <h2 className="mb-6 flex items-center gap-2 text-lg font-semibold text-slate-900">
                <BriefcaseIcon className="h-6 w-6 text-blue-600" aria-hidden />
                Professional details
              </h2>
              <div className="grid grid-cols-1 gap-y-6 gap-x-8 sm:grid-cols-2">
                <Field label="Team" value={teamName || 'Not assigned'} />
                <Field label="Designation" value={user.designation || '—'} />
                <Field label="Department" value={department || '—'} />
                <Field
                  label="Employee ID"
                  value={user.empcode ? `#${user.empcode}` : '—'}
                  mono
                />
                {address ? (
                  <div className="flex flex-col gap-1 sm:col-span-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Address
                    </span>
                    <span className="inline-flex items-start gap-2 text-base font-medium text-slate-900">
                      <MapPinIcon
                        className="mt-0.5 h-5 w-5 shrink-0 text-slate-400"
                        aria-hidden
                      />
                      {address}
                    </span>
                  </div>
                ) : null}
              </div>
            </section>

            <section className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm sm:p-6">
              <h2 className="mb-6 flex items-center gap-2 text-lg font-semibold text-slate-900">
                <IdentificationIcon className="h-6 w-6 text-blue-600" aria-hidden />
                General information
              </h2>
              <div className="grid grid-cols-1 gap-y-6 gap-x-8 sm:grid-cols-2">
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Role
                  </span>
                  <span
                    className={`inline-flex w-fit rounded-full px-2.5 py-1 text-sm font-medium capitalize ${getRoleColor(
                      user.role
                    )}`}
                  >
                    {formatRoleLabel(user.role)}
                  </span>
                </div>
                <Field label="Join date" value={formatDetailDate(joinRaw)} />
                <Field label="Work email" value={email} breakAll />
                <Field
                  label="Record last updated"
                  value={formatDetailDate(updatedAt)}
                />
              </div>
            </section>
          </div>

          <div className="lg:col-span-1">
            <div className="h-full rounded-xl border border-blue-100 bg-gradient-to-b from-blue-50/90 to-slate-50/80 p-6 shadow-sm">
              <h2 className="mb-6 flex items-center gap-2 text-lg font-semibold text-slate-900">
                <EnvelopeIcon className="h-6 w-6 text-blue-600" aria-hidden />
                Contact information
              </h2>
              <div className="space-y-6">
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Email
                  </span>
                  <a
                    href={`mailto:${email}`}
                    className="break-all text-base font-semibold text-blue-800 transition hover:text-blue-600"
                  >
                    {email}
                  </a>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Phone
                  </span>
                  {phoneDisplay ? (
                    <a
                      href={`tel:${String(phoneDisplay).replace(/\s/g, '')}`}
                      className="text-base font-semibold text-slate-900 transition hover:text-blue-700"
                    >
                      {phoneDisplay}
                    </a>
                  ) : (
                    <span className="text-base text-slate-500">Not provided</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const Field = ({ label, value, mono = false, breakAll = false }) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
      {label}
    </span>
    <span
      className={`text-base font-medium text-slate-900 ${mono ? 'font-mono' : ''} ${
        breakAll ? 'break-all' : ''
      }`}
    >
      {value}
    </span>
  </div>
);

export default Profile;
